// public/js/features/assets/assets-filter.js
import { escapeHtml } from '../../utils/escape.js';
import { isAllTenants } from '../../modules/tenant-scope.js';

// ─── Filter configuration ───────────────────────────────────────────────────
// key: field name on the asset object (or special key like 'search', 'hasDevice')
// type: 'text' | 'select'
// dynamic: true = options are loaded at runtime (tenants)
// Static filters; the optional asset-field dropdowns (Asset type, Engine type, …)
// are inserted dynamically from the registry between Search and Status.
export const ASSET_FILTERS = [
    {
        key: 'search', label: 'Search', type: 'text',
        placeholder: 'Search any field — reference, serials, custom fields, devices…'
    },
    {
        key: 'status', label: 'Status', type: 'select',
        options: [
            { value: 'active', text: 'Active' },
            { value: 'inactive', text: 'Inactive' },
        ]
    },
    {
        key: 'tenantId', label: 'Tenant', type: 'select', dynamic: true
    },
    {
        key: 'hasDevice', label: 'Device', type: 'select',
        options: [
            { value: 'yes', text: 'With device' },
            { value: 'no', text: 'Without device' },
        ]
    },
];

// Filter keys handled explicitly in applyFilters; anything else in filterState
// is treated as a direct asset-attribute equality (covers registry fields).
const SPECIAL_FILTER_KEYS = new Set(['search', 'status', 'tenantId', 'hasDevice']);

// Asset keys NOT worth searching (coordinates, timestamps, ids, internal blobs).
// Everything else that is a primitive string/number IS searched, so the search
// covers every active field (reference, serial, custom + fixed fields, registry
// dropdown values, …) plus the paired devices.
const SEARCH_NOISE = new Set([
    'assetUUID', 'assetId', 'tenantId', 'lastLat', 'lastLon', 'lat', 'lon',
    'lastSeen', 'lastUpdated', 'createdAt', 'updatedAt', 'changedAt', 'status',
    'schemaVersion', 'assignedDeviceIds',
]);

// ─── State (survives navigation, cleared on HTML reload) ───────────────────
let filterState = {};

export function getFilterState() { return { ...filterState }; }
export function getActiveFilterCount() { return Object.keys(filterState).length; }
export function clearAllFilters() { filterState = {}; }

export function setFilterValue(key, value) {
    if (value === '' || value == null) delete filterState[key];
    else filterState[key] = value;
}

// ─── Filter logic ───────────────────────────────────────────────────────────
export function applyFilters(assets) {
    return assets.filter(asset => {
        if (filterState.search) {
            const term = filterState.search.toLowerCase();
            const parts = [];
            for (const [k, v] of Object.entries(asset)) {
                if (SEARCH_NOISE.has(k) || v == null) continue;
                if (typeof v === 'string' || typeof v === 'number') parts.push(v);
            }
            parts.push(asset.tenantName);
            for (const d of asset.assignedDevices || []) parts.push(d.deviceId, d.serialNumber, d.imei, d.IMEI);
            const haystack = parts.filter(Boolean).map(s => String(s).toLowerCase()).join(' ');
            if (!haystack.includes(term)) return false;
        }
        if (filterState.status && asset.status !== filterState.status) return false;
        if (filterState.tenantId && asset.tenantId !== filterState.tenantId) return false;
        if (filterState.hasDevice === 'yes' && !(asset.assignedDevices?.length > 0)) return false;
        if (filterState.hasDevice === 'no' && (asset.assignedDevices?.length > 0)) return false;
        // Registry dropdown fields (type, engineType, …): direct equality.
        for (const [k, v] of Object.entries(filterState)) {
            if (SPECIAL_FILTER_KEYS.has(k)) continue;
            if (v && asset[k] !== v) return false;
        }
        return true;
    });
}

// ─── Render panel HTML (once on init) ───────────────────────────────────────
export function renderFilterPanelHTML(tenants = [], regDefs = []) {
    // Build the filter list: Search, then registry dropdowns, then the rest.
    const regFilters = (regDefs || [])
        .slice()
        .sort((a, b) => (a.order ?? 999) - (b.order ?? 999))
        .map(f => ({ key: f.fieldId, label: f.label, type: 'select', options: (f.options || []).map(o => ({ value: o.value, text: o.label })) }));
    let filterList = [ASSET_FILTERS[0], ...regFilters, ...ASSET_FILTERS.slice(1)];
    // Global tenant scope: under a single scope the per-page tenant filter is
    // redundant — it appears only under "All tenants" (as a drill-down). Any
    // stale tenant selection is dropped so it never filters invisibly.
    if (!isAllTenants()) {
        filterList = filterList.filter(f => f.key !== 'tenantId');
        delete filterState.tenantId;
    }

    const rows = filterList.map(f => {
        const val = filterState[f.key] || '';
        let inputHtml = '';

        if (f.type === 'text') {
            inputHtml = `<input type="text"
          class="form-control form-control-sm asset-filter-input"
          data-filter-key="${f.key}"
          placeholder="${f.placeholder || ''}"
          value="${String(val).replace(/"/g, '&quot;')}">`;
        } else {
            const source = f.dynamic
                ? tenants.map(t => ({ value: t.tenantId, text: t.name || t.tenantId }))
                : (f.options || []);
            const opts = [
                `<option value="">— All —</option>`,
                ...source.map(o => `<option value="${escapeHtml(o.value)}" ${val === o.value ? 'selected' : ''}>${escapeHtml(o.text)}</option>`)
            ].join('');
            inputHtml = `<select class="form-select form-select-sm asset-filter-input${f.dynamic ? ' tenant-blur' : ''}" data-filter-key="${f.key}">${opts}</select>`;
        }

        const hasClear = val !== '';
        return `
        <div class="row align-items-center mb-2 filter-row" data-filter-key="${f.key}">
          <label class="col-sm-3 col-md-2 col-form-label col-form-label-sm fw-semibold text-secondary">${escapeHtml(f.label)}</label>
          <div class="col-sm-9 col-md-5">
            <div class="input-group input-group-sm">
              ${inputHtml}
              <button class="btn btn-outline-secondary asset-filter-clear ${hasClear ? '' : 'd-none'}"
                      data-filter-key="${f.key}" title="Clear">
                <i class="fa-solid fa-times"></i>
              </button>
            </div>
          </div>
        </div>`;
    }).join('');

    const activeCount = getActiveFilterCount();
    return `
      <div class="card shadow-sm mb-3" id="assets-filter-card">
        <div class="card-body py-2 px-3">
          <div class="d-flex justify-content-between align-items-center">
            <button class="btn btn-sm btn-link text-secondary text-decoration-none px-0 d-flex align-items-center gap-2"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#assets-filter-collapse"
                    aria-expanded="false"
                    aria-controls="assets-filter-collapse">
              <i class="fa-solid fa-filter"></i>
              <span class="fw-semibold">Filters</span>
              <span id="filter-active-badge" class="badge bg-primary ${activeCount === 0 ? 'd-none' : ''}">${activeCount} active</span>
              <i class="fa-solid fa-chevron-down ms-1 filter-chevron" style="font-size:0.75rem; transition: transform 0.2s;"></i>
            </button>
            <button class="btn btn-sm btn-outline-secondary ${activeCount === 0 ? 'd-none' : ''}" id="btn-clear-all-filters">
              <i class="fa-solid fa-rotate-left me-1"></i>Clear all
            </button>
          </div>
          <div class="collapse" id="assets-filter-collapse">
            <div class="pt-3" id="assets-filter-rows">${rows}</div>
          </div>
        </div>
      </div>`;
}

// ─── Attach event listeners (call after the panel render) ──────────────────
export function attachFilterListeners(onFilterChange) {
    const updateBadgeAndClearAll = () => {
        const count = getActiveFilterCount();
        const badge = document.getElementById('filter-active-badge');
        const clearAll = document.getElementById('btn-clear-all-filters');
        if (badge) { badge.textContent = `${count} active`; badge.classList.toggle('d-none', count === 0); }
        if (clearAll) { clearAll.classList.toggle('d-none', count === 0); }
    };

    // Text inputs — debounced
    document.querySelectorAll('input.asset-filter-input').forEach(el => {
        let timer;
        el.addEventListener('input', () => {
            clearTimeout(timer);
            timer = setTimeout(() => {
                setFilterValue(el.dataset.filterKey, el.value.trim());
                el.closest('.input-group')?.querySelector('.asset-filter-clear')
                    ?.classList.toggle('d-none', !el.value.trim());
                updateBadgeAndClearAll();
                onFilterChange();
            }, 250);
        });
    });

    // Selects — immediate
    document.querySelectorAll('select.asset-filter-input').forEach(el => {
        el.addEventListener('change', () => {
            setFilterValue(el.dataset.filterKey, el.value);
            el.closest('.input-group')?.querySelector('.asset-filter-clear')
                ?.classList.toggle('d-none', !el.value);
            updateBadgeAndClearAll();
            onFilterChange();
        });
    });

    // Clear a single field
    document.querySelectorAll('.asset-filter-clear').forEach(btn => {
        btn.addEventListener('click', () => {
            const key = btn.dataset.filterKey;
            setFilterValue(key, '');
            const row = document.querySelector(`.filter-row[data-filter-key="${key}"]`);
            if (row) { const inp = row.querySelector('.asset-filter-input'); if (inp) inp.value = ''; }
            btn.classList.add('d-none');
            updateBadgeAndClearAll();
            onFilterChange();
        });
    });

    // Clear all
    document.getElementById('btn-clear-all-filters')?.addEventListener('click', () => {
        clearAllFilters();
        document.querySelectorAll('.asset-filter-input').forEach(el => { el.value = ''; });
        document.querySelectorAll('.asset-filter-clear').forEach(b => b.classList.add('d-none'));
        updateBadgeAndClearAll();
        onFilterChange();
    });

    // Rotate the chevron on expand/collapse
    const collapseEl = document.getElementById('assets-filter-collapse');
    if (collapseEl) {
        collapseEl.addEventListener('show.bs.collapse', () => {
            document.querySelector('.filter-chevron')?.style.setProperty('transform', 'rotate(180deg)');
        });
        collapseEl.addEventListener('hide.bs.collapse', () => {
            document.querySelector('.filter-chevron')?.style.setProperty('transform', 'rotate(0deg)');
        });
    }


}